/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { OrgUnitContact } from '@msdyn365-commerce/retail-proxy';

/**
 * Node-type classification for a Cross Legal Entity inventory node.
 */
export enum CrossEntityNodeType {
    warehouse = 'warehouse',
    distributor = 'distributor',
    retailStore = 'retailStore'
}

/**
 * Stock status derived client-side from raw available quantity and the configured thresholds.
 */
export type CrossEntityStockStatus = 'inStock' | 'lowStock' | 'outOfStock';

/**
 * Result of client-side stock-label derivation.
 */
export interface ICrossEntityStockLabel {
    status: CrossEntityStockStatus;

    /**
     * Quantity to display. Only populated for the low-stock state.
     */
    quantity?: number;
}

/**
 * A single Cross Legal Entity inventory / location node, normalized from the proxy entities.
 */
export interface ICrossEntityInventoryNode {
    // identity
    orgUnitNumber?: string;
    orgUnitName?: string;
    dataAreaId?: string;
    channelId?: number;
    inventoryLocationId?: string;
    inventorySiteId?: string;

    // address / geo
    address?: string;
    street?: string;
    city?: string;
    county?: string;
    state?: string;
    stateName?: string;
    zip?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    distance?: number;
    distanceUnitResultValue?: number;

    // fulfillment capability
    isPickupLocation?: boolean;
    isShippingLocation?: boolean;

    // classification (node-type / region) — best-effort, may be undefined
    nodeType?: CrossEntityNodeType;
    region?: string;

    // availability — undefined for location-only (top-ribbon) results
    availableQuantity?: number;
    orderedSum?: number;
    physicalReserved?: number;
    unitOfMeasure?: string;
    inventoryUnitOfMeasureDescription?: string;

    // contacts (returned on OrgUnitLocation)
    contacts?: OrgUnitContact[];
}

/**
 * Aggregate result returned by the Cross Legal Entity data actions.
 */
export interface ICrossEntityInventoryResult {
    /**
     * Populated only for product-scoped (PDP) results.
     */
    productId?: number;
    nodes: ICrossEntityInventoryNode[];
}

/**
 * Client-side filter state held by the locator module.
 */
export interface ICrossEntityLocatorFilterState {
    searchText?: string;
    latitude?: number;
    longitude?: number;
    radius?: number;
    selectedRegion?: string;
    selectedDataAreaId?: string;
    selectedNodeType?: CrossEntityNodeType;
    inStockOnly?: boolean;
}

/**
 * Returns a stable identity key for a node. The same org-unit number/name can repeat across
 * legal entities, so identity is keyed by (dataAreaId, inventoryLocationId) with sensible fallbacks.
 * @param node - The node to key.
 * @returns A unique string key.
 */
export const getCrossEntityNodeKey = (node: ICrossEntityInventoryNode): string => {
    const dataAreaId = node.dataAreaId ?? '';
    const locationId = node.inventoryLocationId ?? node.orgUnitNumber ?? '';
    return `${dataAreaId}::${locationId}`;
};
